import { createContext, forwardRef, useContext } from "react";

import * as BasePhoneInput from "react-phone-number-input";
import flags from "react-phone-number-input/flags";
import { EarthIcon } from "lucide-react";

import { buttonVariants } from "@/components/ui/button";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxSeparator,
  ComboboxTrigger,
  ComboboxValue,
} from "@/components/ui/combobox";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

const PhoneInputContext = createContext({
  variant: "default",
  popupClassName: undefined,
  scrollAreaClassName: undefined,
  inputClassName: undefined,
  triggerClassName: undefined,
  readOnly: false,
});

const PhoneInput = ({
  className,
  variant,
  popupClassName,
  scrollAreaClassName,
  inputClassName,
  triggerClassName,
  onChange,
  value,
  ...props
}) => {
  const phoneInputSize = variant || "default";

  return (
    <PhoneInputContext.Provider
      value={{
        variant: phoneInputSize,
        popupClassName,
        scrollAreaClassName,
        inputClassName,
        triggerClassName,
        readOnly: props.readOnly,
      }}
    >
      <BasePhoneInput.default
        className={cn(
          "tw:flex",
          props["aria-invalid"] &&
            "tw:[&_*[data-slot=combobox-trigger]]:border-destructive tw:[&_*[data-slot=combobox-trigger]]:ring-destructive/50",
          className
        )}
        flagComponent={FlagComponent}
        countrySelectComponent={CountrySelect}
        inputComponent={InputComponent}
        smartCaret={false}
        value={value || undefined}
        onChange={value => onChange?.(value || "")}
        {...props}
      />
    </PhoneInputContext.Provider>
  );
};

// react-phone-number-input keeps a ref to this input and calls .focus() on it
// after a country change, so the ref must reach the DOM element.
const InputComponent = forwardRef(({ className, ...props }, ref) => {
  const { variant, inputClassName } = useContext(PhoneInputContext);

  return (
    <Input
      ref={ref}
      className={cn(
        "tw:rounded-l-none tw:focus:z-1",
        variant === "sm" && "tw:h-auto! tw:py-0!",
        variant === "lg" && "tw:h-auto!",
        inputClassName,
        className
      )}
      {...props}
    />
  );
});

const CountrySelect = ({
  disabled,
  value: selectedCountry,
  options: countryList,
  onChange,
}) => {
  const { variant, popupClassName, triggerClassName, readOnly } =
    useContext(PhoneInputContext);

  // react-phone-number-input includes an "International" entry with no value;
  // drop it so every combobox item is a selectable country.
  const countries = countryList.filter(country => country.value);
  const selectedItem =
    countries.find(country => country.value === selectedCountry) ?? null;

  const handleChange = country => {
    country?.value && onChange(country.value);
  };

  return (
    <Combobox
      items={countries}
      value={selectedItem}
      onValueChange={handleChange}
      itemToStringLabel={country => country?.label ?? ""}
      isItemEqualToValue={(country, selected) =>
        country.value === selected?.value
      }
      disabled={disabled || readOnly}
    >
      <ComboboxTrigger
        className={buttonVariants({
          variant: "outline",
          size: variant,
          className: cn(
            "tw:flex tw:gap-1 tw:rounded-r-none tw:border-e-0 tw:px-2.5 tw:py-0 tw:leading-none tw:hover:bg-transparent tw:focus:z-10 tw:aria-pressed:bg-transparent tw:[&>svg]:hidden",
            disabled && "tw:dark:disabled:bg-input/80",
            triggerClassName
          ),
        })}
        disabled={disabled || readOnly}
      >
        <span className="tw:sr-only">
          <ComboboxValue />
        </span>
        <FlagComponent
          country={selectedCountry}
          countryName={selectedCountry}
        />
      </ComboboxTrigger>
      <ComboboxContent
        className={cn(
          "tw:w-xs tw:*:data-[slot=input-group]:bg-transparent",
          popupClassName
        )}
      >
        <ComboboxInput
          placeholder="e.g. United States"
          showTrigger={false}
          className="tw:border-input tw:focus-visible:border-border tw:rounded-none tw:border-0 tw:px-0 tw:py-2.5 tw:shadow-none tw:ring-0! tw:outline-none! tw:focus-visible:ring-0 tw:focus-visible:ring-offset-0"
        />
        <ComboboxSeparator />
        <ComboboxEmpty className="tw:px-4 tw:py-2.5 tw:text-sm">
          No country found.
        </ComboboxEmpty>
        <ComboboxList>
          {country => (
            <ComboboxItem
              key={country.value}
              value={country}
              className="tw:flex tw:items-center tw:gap-2"
            >
              <FlagComponent
                country={country.value}
                countryName={country.label}
              />
              <span className="tw:flex-1 tw:text-sm">{country.label}</span>
              <span className="tw:text-foreground/50 tw:text-sm">
                {`+${BasePhoneInput.getCountryCallingCode(country.value)}`}
              </span>
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
};

const FlagComponent = ({ country, countryName }) => {
  const Flag = flags[country];

  return (
    <span className="tw:flex tw:h-4 tw:w-4 tw:items-center tw:justify-center tw:[&_svg:not([class*='size-'])]:size-full! tw:[&_svg:not([class*='size-'])]:rounded-[5px]">
      {Flag ? (
        <Flag title={countryName} />
      ) : (
        <EarthIcon className="tw:size-4 tw:opacity-60" />
      )}
    </span>
  );
};

export { PhoneInput };
